<app-header></app-header>
<div class="my-listings-container">
  <div class="page-header">
    <div class="header-content">
      <div class="header-left">
        <h1 class="page-title">My Listings</h1>
        <p class="page-subtitle">
          Manage all your active listings across all categories
        </p>
      </div>
    </div>
  </div>

  <div *ngIf="messages.success" class="alert alert-success">
    <span>{{ messages.success }}</span>
    <button class="alert-close" (click)="dismissMessage('success')">×</button>
  </div>

  <div *ngIf="messages.error" class="alert alert-error">
    <span>{{ messages.error }}</span>
    <button class="alert-close" (click)="dismissMessage('error')">×</button>
  </div>

  <div *ngIf="loading" class="loading-container">
    <div class="loading-spinner"></div>
    <p>Loading your listings...</p>
  </div>

  <div *ngIf="error && !loading" class="error-container">
    <div class="error-card">
      <h3>Something went wrong</h3>
      <p>{{ error }}</p>
      <button class="btn-retry" (click)="onRefresh()">Try Again</button>
    </div>
  </div>

  <div *ngIf="!loading && !error" class="main-content">
    <div class="stats-summary">
      <div class="stat-card">
        <span class="stat-number">{{ getTotalListings() }}</span>
        <span class="stat-label">Total Listings</span>
      </div>
      <div class="stat-card">
        <span class="stat-number">{{ allListingsCounts.vehicles.length }}</span>
        <span class="stat-label">Vehicles</span>
      </div>
      <div class="stat-card">
        <span class="stat-number">{{
          allListingsCounts.realEstate.length
        }}</span>
        <span class="stat-label">Real Estate</span>
      </div>
      <div class="stat-card">
        <span class="stat-number">{{
          allListingsCounts.marketplace.length
        }}</span>
        <span class="stat-label">Marketplace</span>
      </div>
      <div class="stat-card">
        <span class="stat-number">{{ allListingsCounts.jobs.length }}</span>
        <span class="stat-label">Jobs</span>
      </div>
    </div>

    <div class="controls-bar">
      <div class="view-toggle">
        <button
          class="toggle-btn"
          [class.active]="viewMode === 'all'"
          (click)="setViewMode('all')"
        >
          All Listings
        </button>
        <button
          class="toggle-btn"
          [class.active]="viewMode === 'category'"
          (click)="setViewMode('category')"
        >
          By Category
        </button>
      </div>

      <div class="status-filter" *ngIf="viewMode === 'all'">
        <label for="status-select">Filter by status:</label>
        <select
          id="status-select"
          [(ngModel)]="statusFilter"
          (ngModelChange)="onStatusFilterChange($event)"
          class="status-select"
        >
          <option *ngFor="let option of statusOptions" [value]="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>

    <div *ngIf="getTotalListings() === 0" class="empty-state">
      <div class="empty-content">
        <div class="empty-icon">📋</div>
        <h3>No listings yet</h3>
        <p>
          You haven't created any listings yet. Start selling by creating your
          first listing.
        </p>
        <a routerLink="/sell" class="btn-create-listing"
          >Create Your First Listing</a
        >
      </div>
    </div>

    <div *ngIf="viewMode === 'all' && getTotalListings() > 0">
      <div *ngIf="sortedListings.length > 0" class="listings-grid">
        <div
          *ngFor="let listing of sortedListings; trackBy: trackByListingId"
          class="listing-card clickable-card"
          (mouseenter)="onListingHover(listing)"
          (mouseleave)="onListingLeave(listing)"
        >
          <div
            class="listing-image"
            (touchstart)="onTouchStart($event, listing)"
            (touchmove)="onTouchMove($event, listing)"
            (touchend)="onTouchEnd($event, listing)"
          >
            <div class="image-wrapper" [class.loading]="listing.isImageLoading">
              <img
                [src]="listing.images[listing.currentImageIndex]"
                [alt]="listing.title"
                class="listing-img"
                loading="lazy"
                (load)="
                  onImageLoad(
                    listing.images[listing.currentImageIndex],
                    listing
                  )
                "
              />

              <div class="image-loading-overlay" *ngIf="listing.isImageLoading">
                <div class="spinner-small"></div>
              </div>
            </div>

            <div
              class="image-click-zones desktop-only"
              *ngIf="hasMultipleImages(listing)"
            >
              <div
                class="click-zone zone-left"
                (click)="onImageZoneClick($event, listing, 'prev')"
                [attr.aria-label]="'Previous image'"
              >
                <div class="zone-icon">
                  <svg viewBox="0 0 24 24" class="zone-svg">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </div>
              </div>
              <div
                class="click-zone zone-middle"
                (click)="onImageZoneClick($event, listing, 'view')"
              ></div>
              <div
                class="click-zone zone-right"
                (click)="onImageZoneClick($event, listing, 'next')"
                [attr.aria-label]="'Next image'"
              >
                <div class="zone-icon">
                  <svg viewBox="0 0 24 24" class="zone-svg">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              </div>
            </div>

            <div
              class="carousel-controls desktop-only"
              *ngIf="hasMultipleImages(listing) && listing.showCarouselControls"
              [class.fading-out]="!listing.showCarouselControls"
            >
              <div class="carousel-indicator">
                <svg viewBox="0 0 24 24" class="carousel-icon">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </div>

              <div class="carousel-indicator">
                <svg viewBox="0 0 24 24" class="carousel-icon">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </div>

            <div
              class="image-counter desktop-only"
              *ngIf="hasMultipleImages(listing)"
            >
              {{ getImageCounter(listing) }}
            </div>

            <div
              class="image-dots mobile-only"
              *ngIf="hasMultipleImages(listing)"
            >
              <span
                *ngFor="let img of listing.images; let i = index"
                class="dot"
                [class.active]="i === listing.currentImageIndex"
              ></span>
            </div>

            <div class="image-overlay">
              <span class="category-badge">{{
                getCategoryName(listing.table_name)
              }}</span>
              <span
                class="status-badge"
                [class]="getStatusClass(listing.status)"
              >
                {{ listing.status }}
              </span>
            </div>
          </div>

          <div class="listing-content" (click)="onCardClick(listing, $event)">
            <h3 class="listing-title">{{ listing.title }}</h3>
            <p class="listing-subcategory">
              {{ getSubcategoryName(listing.table_name) }}
            </p>

            <div class="listing-meta">
              <span class="listing-price">{{
                formatPrice(listing.price, listing.currency)
              }}</span>
              <span class="listing-date">{{
                formatDate(listing.created_at)
              }}</span>
            </div>

            <div class="listing-stats" *ngIf="listing.has_images">
              <span class="stat-item"> {{ listing.image_count }} photos </span>
            </div>
          </div>
        </div>
      </div>

      <div
        *ngIf="paginatedAllListings.totalCount > 0 || statusFilter !== 'all'"
        class="pagination-container"
      >
        <div class="pagination-info">
          <span *ngIf="paginatedAllListings.totalPages > 1">
            Page {{ paginatedAllListings.currentPage }} of
            {{ paginatedAllListings.totalPages }}
          </span>
          <span
            *ngIf="
              paginatedAllListings.totalPages <= 1 &&
              paginatedAllListings.totalCount > 0
            "
            class="pagination-single"
          >
            {{ paginatedAllListings.totalCount }}
            {{ statusFilter !== "all" ? statusFilter : "" }} listing{{
              paginatedAllListings.totalCount === 1 ? "" : "s"
            }}{{ statusFilter !== "all" ? " found" : "" }}
          </span>
          <span
            *ngIf="
              paginatedAllListings.totalCount === 0 && statusFilter !== 'all'
            "
            class="pagination-single"
          >
            No {{ statusFilter }} listings found
          </span>
          <span
            class="pagination-total"
            *ngIf="paginatedAllListings.totalPages > 1"
          >
            Showing {{ sortedListings.length }} of
            {{ paginatedAllListings.totalCount }}
            {{ statusFilter !== "all" ? statusFilter + " " : "" }}listings
          </span>
        </div>

        <div
          class="pagination-controls"
          *ngIf="paginatedAllListings.totalPages > 1"
        >
          <button
            class="pagination-btn prev"
            (click)="goToPreviousPage()"
            [disabled]="!paginatedAllListings.hasPreviousPage"
            [attr.aria-label]="'Go to previous page'"
            title="Previous page"
          >
            <span>Previous</span>
          </button>

          <div class="pagination-numbers">
            <button
              *ngFor="let page of getVisiblePageNumbers()"
              class="pagination-number"
              [class.active]="page === paginatedAllListings.currentPage"
              [class.dots]="page === -1"
              (click)="page !== -1 && goToPage(page)"
              [disabled]="page === -1"
              [attr.aria-label]="page === -1 ? null : 'Go to page ' + page"
              [attr.aria-current]="
                page === paginatedAllListings.currentPage ? 'page' : null
              "
            >
              {{ page === -1 ? "..." : page }}
            </button>
          </div>

          <button
            class="pagination-btn next"
            (click)="goToNextPage()"
            [disabled]="!paginatedAllListings.hasNextPage"
            [attr.aria-label]="'Go to next page'"
            title="Next page"
          >
            <span>Next</span>
          </button>
        </div>
      </div>

      <div
        *ngIf="paginatedAllListings.totalCount === 0 && statusFilter !== 'all'"
        class="no-results"
      >
        <h3>No {{ statusFilter }} listings found</h3>
        <p>
          You don't have any {{ statusFilter }} listings. Try changing the
          status filter to see other listings.
        </p>
      </div>
    </div>

    <div
      *ngIf="viewMode === 'category' && getTotalListings() > 0"
      class="category-view"
    >
      <div
        *ngIf="allCategoryListings.vehicles.length > 0"
        class="category-section"
      >
        <h2 class="category-title">
          Vehicles
          <span class="count"
            >({{ allListingsCounts.vehicles.length }} total, showing
            {{ allCategoryListings.vehicles.length }})</span
          >
        </h2>
        <div class="category-listings">
          <div
            *ngFor="
              let listing of allCategoryListings.vehicles;
              trackBy: trackByListingId
            "
            class="listing-row clickable-row"
            (click)="onCardClick(listing, $event)"
          >
            <div class="listing-info">
              <img
                [src]="getListingImage(listing)"
                [alt]="listing.title"
                class="listing-thumb"
                loading="lazy"
              />
              <div class="listing-details">
                <h4>{{ listing.title }}</h4>
                <p>{{ getSubcategoryName(listing.table_name) }}</p>
                <span class="price">{{
                  formatPrice(listing.price, listing.currency)
                }}</span>
              </div>
            </div>
            <div class="listing-status">
              <span
                class="status-badge"
                [class]="getStatusClass(listing.status)"
              >
                {{ listing.status }}
              </span>
              <span class="date">{{ formatDate(listing.created_at) }}</span>
            </div>
          </div>

          <div *ngIf="hasMoreListings('vehicles')" class="load-more-container">
            <button
              class="btn-load-more"
              (click)="loadMoreListings('vehicles')"
              [disabled]="isLoadingMore('vehicles')"
            >
              <span *ngIf="!isLoadingMore('vehicles')">Load More Vehicles</span>
              <span *ngIf="isLoadingMore('vehicles')">
                <span class="loading-spinner-small"></span>
                Loading...
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        *ngIf="allCategoryListings.realEstate.length > 0"
        class="category-section"
      >
        <h2 class="category-title">
          Real Estate
          <span class="count"
            >({{ allListingsCounts.realEstate.length }} total, showing
            {{ allCategoryListings.realEstate.length }})</span
          >
        </h2>
        <div class="category-listings">
          <div
            *ngFor="
              let listing of allCategoryListings.realEstate;
              trackBy: trackByListingId
            "
            class="listing-row clickable-row"
            (click)="onCardClick(listing, $event)"
          >
            <div class="listing-info">
              <img
                [src]="getListingImage(listing)"
                [alt]="listing.title"
                class="listing-thumb"
                loading="lazy"
              />
              <div class="listing-details">
                <h4>{{ listing.title }}</h4>
                <p>{{ getSubcategoryName(listing.table_name) }}</p>
                <span class="price">{{
                  formatPrice(listing.price, listing.currency)
                }}</span>
              </div>
            </div>
            <div class="listing-status">
              <span
                class="status-badge"
                [class]="getStatusClass(listing.status)"
              >
                {{ listing.status }}
              </span>
              <span class="date">{{ formatDate(listing.created_at) }}</span>
            </div>
          </div>

          <div
            *ngIf="hasMoreListings('realEstate')"
            class="load-more-container"
          >
            <button
              class="btn-load-more"
              (click)="loadMoreListings('realEstate')"
              [disabled]="isLoadingMore('realEstate')"
            >
              <span *ngIf="!isLoadingMore('realEstate')"
                >Load More Real Estate</span
              >
              <span *ngIf="isLoadingMore('realEstate')">
                <span class="loading-spinner-small"></span>
                Loading...
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        *ngIf="allCategoryListings.marketplace.length > 0"
        class="category-section"
      >
        <h2 class="category-title">
          Marketplace
          <span class="count"
            >({{ allListingsCounts.marketplace.length }} total, showing
            {{ allCategoryListings.marketplace.length }})</span
          >
        </h2>
        <div class="category-listings">
          <div
            *ngFor="
              let listing of allCategoryListings.marketplace;
              trackBy: trackByListingId
            "
            class="listing-row clickable-row"
            (click)="onCardClick(listing, $event)"
          >
            <div class="listing-info">
              <img
                [src]="getListingImage(listing)"
                [alt]="listing.title"
                class="listing-thumb"
                loading="lazy"
              />
              <div class="listing-details">
                <h4>{{ listing.title }}</h4>
                <p>{{ getSubcategoryName(listing.table_name) }}</p>
                <span class="price">{{
                  formatPrice(listing.price, listing.currency)
                }}</span>
              </div>
            </div>
            <div class="listing-status">
              <span
                class="status-badge"
                [class]="getStatusClass(listing.status)"
              >
                {{ listing.status }}
              </span>
              <span class="date">{{ formatDate(listing.created_at) }}</span>
            </div>
          </div>

          <div
            *ngIf="hasMoreListings('marketplace')"
            class="load-more-container"
          >
            <button
              class="btn-load-more"
              (click)="loadMoreListings('marketplace')"
              [disabled]="isLoadingMore('marketplace')"
            >
              <span *ngIf="!isLoadingMore('marketplace')"
                >Load More Marketplace Items</span
              >
              <span *ngIf="isLoadingMore('marketplace')">
                <span class="loading-spinner-small"></span>
                Loading...
              </span>
            </button>
          </div>
        </div>
      </div>

      <div *ngIf="allCategoryListings.jobs.length > 0" class="category-section">
        <h2 class="category-title">
          Jobs
          <span class="count"
            >({{ allListingsCounts.jobs.length }} total, showing
            {{ allCategoryListings.jobs.length }})</span
          >
        </h2>
        <div class="category-listings">
          <div
            *ngFor="
              let listing of allCategoryListings.jobs;
              trackBy: trackByListingId
            "
            class="listing-row clickable-row"
            (click)="onCardClick(listing, $event)"
          >
            <div class="listing-info">
              <img
                [src]="getListingImage(listing)"
                [alt]="listing.title"
                class="listing-thumb"
                loading="lazy"
              />
              <div class="listing-details">
                <h4>{{ listing.title }}</h4>
                <p>{{ getSubcategoryName(listing.table_name) }}</p>
                <span class="price">{{
                  formatPrice(listing.price, listing.currency)
                }}</span>
              </div>
            </div>
            <div class="listing-status">
              <span
                class="status-badge"
                [class]="getStatusClass(listing.status)"
              >
                {{ listing.status }}
              </span>
              <span class="date">{{ formatDate(listing.created_at) }}</span>
            </div>
          </div>

          <div *ngIf="hasMoreListings('jobs')" class="load-more-container">
            <button
              class="btn-load-more"
              (click)="loadMoreListings('jobs')"
              [disabled]="isLoadingMore('jobs')"
            >
              <span *ngIf="!isLoadingMore('jobs')">Load More Jobs</span>
              <span *ngIf="isLoadingMore('jobs')">
                <span class="loading-spinner-small"></span>
                Loading...
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        *ngIf="
          allCategoryListings.vehicles.length === 0 &&
          allListingsCounts.vehicles.length > 0
        "
        class="category-section"
      >
        <h2 class="category-title">
          Vehicles
          <span class="count"
            >({{ allListingsCounts.vehicles.length }} total)</span
          >
        </h2>
        <div class="category-empty">
          <p>Loading vehicles...</p>
        </div>
      </div>

      <div
        *ngIf="
          allCategoryListings.realEstate.length === 0 &&
          allListingsCounts.realEstate.length > 0
        "
        class="category-section"
      >
        <h2 class="category-title">
          Real Estate
          <span class="count"
            >({{ allListingsCounts.realEstate.length }} total)</span
          >
        </h2>
        <div class="category-empty">
          <p>Loading real estate listings...</p>
        </div>
      </div>

      <div
        *ngIf="
          allCategoryListings.marketplace.length === 0 &&
          allListingsCounts.marketplace.length > 0
        "
        class="category-section"
      >
        <h2 class="category-title">
          Marketplace
          <span class="count"
            >({{ allListingsCounts.marketplace.length }} total)</span
          >
        </h2>
        <div class="category-empty">
          <p>Loading marketplace items...</p>
        </div>
      </div>

      <div
        *ngIf="
          allCategoryListings.jobs.length === 0 &&
          allListingsCounts.jobs.length > 0
        "
        class="category-section"
      >
        <h2 class="category-title">
          Jobs
          <span class="count">({{ allListingsCounts.jobs.length }} total)</span>
        </h2>
        <div class="category-empty">
          <p>Loading job listings...</p>
        </div>
      </div>
    </div>
  </div>
</div>
<app-footer></app-footer>
